/**
 * <s-product-gallery> — single-stage product gallery.
 *
 * Shows one media item at a time. Thumbnails (a tablist with roving tabindex)
 * swap the stage, and option changes in the product form swap it to the
 * selected variant's image using the variant→media map, without a fetch.
 *
 * Required markup:
 *   script[data-variant-media]  — JSON { variantId: mediaId } for the product.
 *   [role="tabpanel"][data-media-id] — one stage slide per media item; inactive slides are [hidden].
 *   [role="tab"][data-media-id]      — optional thumbnail buttons inside [data-gallery-thumbs].
 *
 * Listens for bubbling `change` events on the closest s-view-event (the product
 * root shared with s-variant-picker) and reads data-variant-id the same way the picker does.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #variantMedia = {};
  #activeId = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    try {
      this.#variantMedia = JSON.parse(this.querySelector('[data-variant-media]')?.textContent || '{}');
    } catch {
      this.#variantMedia = {};
    }
    this.#activeId = this.querySelector('[role="tabpanel"]:not([hidden])')?.dataset.mediaId ?? null;

    this.addEventListener('click', this.#onThumbClick, opts);
    this.addEventListener('keydown', this.#onThumbKeydown, opts);

    const root = this.closest('s-view-event') || this.parentElement?.parentElement;
    root?.addEventListener('change', this.#onOptionChange, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #onOptionChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLElement) || this.contains(input)) return;
    const variantId = input.dataset.variantId || input.selectedOptions?.[0]?.dataset?.variantId;
    const mediaId = variantId && this.#variantMedia[variantId];
    if (mediaId) this.show(mediaId);
  };

  #tabs() {
    return Array.from(this.querySelectorAll('[role="tab"][data-media-id]'));
  }

  #onThumbClick = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"][data-media-id]') : null;
    if (!tab || !this.contains(tab)) return;
    this.show(tab.dataset.mediaId);
  };

  #onThumbKeydown = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (!tab) return;
    const tabs = this.#tabs();
    const index = tabs.indexOf(tab);
    if (index < 0) return;

    const rtl = getComputedStyle(this).direction === 'rtl';
    let next = null;
    if (event.key === (rtl ? 'ArrowLeft' : 'ArrowRight')) next = tabs[(index + 1) % tabs.length];
    else if (event.key === (rtl ? 'ArrowRight' : 'ArrowLeft')) next = tabs[(index - 1 + tabs.length) % tabs.length];
    else if (event.key === 'Home') next = tabs[0];
    else if (event.key === 'End') next = tabs[tabs.length - 1];
    if (!next) return;

    event.preventDefault();
    this.show(next.dataset.mediaId);
    next.focus();
  };

  // Scroll only the thumbnail strip, never the page.
  #revealThumb(tab, reduceMotion) {
    const strip = tab.parentElement;
    if (!strip) return;
    const start = tab.offsetLeft - strip.offsetLeft;
    const end = start + tab.offsetWidth;
    let left = null;
    if (start < strip.scrollLeft) left = start;
    else if (end > strip.scrollLeft + strip.clientWidth) left = end - strip.clientWidth;
    if (left !== null) strip.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  show(mediaId) {
    mediaId = String(mediaId);
    if (mediaId === this.#activeId) return;

    const incoming = this.querySelector(`[role="tabpanel"][data-media-id="${CSS.escape(mediaId)}"]`);
    if (!incoming) return;
    const outgoing = this.#activeId
      ? this.querySelector(`[role="tabpanel"][data-media-id="${CSS.escape(this.#activeId)}"]`)
      : null;

    this.#activeId = mediaId;

    // Make the incoming image load now rather than waiting for lazy-loading heuristics.
    incoming.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    incoming.hidden = false;

    // Pause any playing video on the slide being hidden.
    outgoing?.querySelectorAll('video').forEach((video) => video.pause());

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (outgoing && !reduceMotion && typeof incoming.animate === 'function') {
      incoming.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: 'cubic-bezier(0.215, 0.61, 0.355, 1)' });
    }
    if (outgoing) outgoing.hidden = true;

    for (const tab of this.#tabs()) {
      const selected = tab.dataset.mediaId === mediaId;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
      if (selected) this.#revealThumb(tab, reduceMotion);
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
